import React, { useState, useEffect } from 'react';
import {
  DataGrid,
} from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import 'moment/locale/tr';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import {
  createCouponCode,
  getCouponCodes,
  updateCouponCode,
  uploadCouponCodes,
  getBranches,
  getGroupCodes,
} from '../api/api';

moment.locale('tr');

const DATE_FORMAT = "DD.MM.YYYY";



const CHANNEL_OPTIONS = [
  { name: 'Web', value: 10 },
  { name: 'Branch', value: 20 },
  { name: 'Call Center', value: 30 },
  { name: 'Mobile', value: 50 },
  { name: 'Tablet', value: 40 },
  { name: 'Job', value: 60 },
];

const RESERVATION_OPTIONS = [
  { name: 'Bireysel', value: 10 },
  { name: 'Kurumsal', value: 20 },
  { name: 'Acente', value: 30 },
  { name: 'Broker', value: 40 },
];

const TYPE_OPTIONS = [
  { name: 'Ratio', value: 0 },
  { name: 'Amount', value: 1 },
];

const STATUS_OPTIONS = [
  { name: 'Active', value: 1 },
  { name: 'Passive', value: 0 },
];


const couponSchema = yup.object().shape({
  couponcode: yup.string().required('Kupon Kodu zorunludur.'),
  name: yup.string().required('Kupon Adı zorunludur.'),
  type: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .required('Kupon Tipi zorunludur.'),
  status: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .required('Kupon Durumu zorunludur.'),
  payLaterDiscountValue: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('Sayısal bir değer girilmelidir.')
    .required('Sonra Öde İndirim Oranı zorunludur.'),
  payNowDiscountValue: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('Sayısal bir değer girilmelidir.')
    .required('Hemen Öde İndirim Oranı zorunludur.'),
  reservationType: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .required('Rezervasyon Tipi zorunludur.'),
  validityStartDate: yup
    .string()
    .required('Başlangıç Tarihi zorunludur.')
    .test(
      'is-date-valid',
      'Geçersiz tarih formatı (DD.MM.YYYY)',
      (value) => moment(value, DATE_FORMAT, true).isValid()
    ),
  validityEndDate: yup
    .string()
    .required('Bitiş Tarihi zorunludur.')
    .test(
      'is-date-valid',
      'Geçersiz tarih formatı (DD.MM.YYYY)',
      (value) => moment(value, DATE_FORMAT, true).isValid()
    ),
  branches: yup
    .array()
    .of(yup.string())
    .min(1, 'En az bir Ofis seçimi zorunludur.')
    .required('Geçerli Ofisler zorunludur.'),
  channels: yup
    .array()
    .of(yup.number())
    .min(1, 'En az bir Kanal seçimi zorunludur.')
    .required('Geçerli Kanallar zorunludur.'),
  groupCodes: yup
    .array()
    .of(yup.string())
    .min(1, 'En az bir Grup Kodu seçimi zorunludur.')
    .required('Grup Kodları zorunludur.'),
});

const getEmptyFormState = () => ({
  couponcode: '',
  name: '',
  numberOfCoupons: '',
  validityStartDate: '',
  validityEndDate: '',
  startDate: '',
  endDate: '',
  type: '',
  status: '',
  payLaterDiscountValue: '',
  payNowDiscountValue: '',
  reservationType: '',
  branches: [],
  channels: [],
  groupCodes: [],
});

const CouponCodeManagement = () => {
  // State Management
  const [couponCodes, setCouponCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openExcelDialog, setOpenExcelDialog] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [groupCodeOptions, setGroupCodeOptions] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [selectedCouponCode, setSelectedCouponCode] = useState(getEmptyFormState());

  // Component Mount
  useEffect(() => {
    fetchCouponCodes();
    fetchBranches();
    fetchGroupCodes();
  }, []);

  // API Calls
  const fetchCouponCodes = async () => {
    setLoading(true);
    try {
      const response = await getCouponCodes();
      if (response?.isSuccess && response?.value) {
        // response.value.forEach(coupon => { 
        //     coupon.branches[0].branchId = "7c10d8e1-83f6-ea11-a815-000d3aafb97e";
        //     coupon.branches[1].branchId = "42161068-ce8f-ea11-a811-000d3a2e9b6c";
        //  });
        setCouponCodes(response.value);
      } else {
        toast.error('Kupon kodları alınamadı');
      }
    } catch (error) {
      toast.error('Hata: ' + (error?.message || 'Kupon kodları yüklenirken hata oluştu'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await getBranches();
      if (response?.isSuccess && response?.value?.branchs) {
        setBranchesOptions(response.value.branchs);
      }
    } catch (error) {
      toast.error('Şubeler yüklenirken hata oluştu');
      console.error(error);
    }
  };

  const fetchGroupCodes = async () => {
    try {
      const response = await getGroupCodes();
      if (response?.isSuccess && response?.value) {
        setGroupCodeOptions(response.value);
      }
    } catch (error) {
      toast.error('Grup kodları yüklenirken hata oluştu');
      console.error(error);
    }
  };

  // Dialog Functions
  const openCreateDialog = () => {
    setSelectedCouponCode(getEmptyFormState());
    setIsUpdate(false);
    setValidationErrors({});
    setOpenDialog(true);
  };

  const openUpdateDialog = (couponCode) => {
    const normalized = {
      ...couponCode,
      couponcode: couponCode.couponcode || couponCode.code || '',
      numberOfCoupons: couponCode.numberOfCoupons || '',
      branches: Array.isArray(couponCode.branches)
        ? couponCode.branches.map((b) => b.branchId || b.id)
        : [],
      channels: Array.isArray(couponCode.channels)
        ? couponCode.channels.map((c) => c)
        : [],
      groupCodes: Array.isArray(couponCode.groupCodes)
        ? couponCode.groupCodes.map((g) => g.groupCodeInformationId || g.id)
        : [],
      type: couponCode.type !== undefined ? couponCode.type : '',
      status: couponCode.status !== undefined ? couponCode.status : '',
      reservationType: couponCode.reservationType !== undefined ? Number(couponCode.reservationType) : '',
    };
    setSelectedCouponCode(normalized);
    setIsUpdate(true);
    setValidationErrors({});
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedCouponCode(getEmptyFormState());
    setValidationErrors({});
  };

  const closeExcelDialog = () => {
    setOpenExcelDialog(false);
    setExcelFile(null);
  };

  const handleSave = async () => {
    try {
      // Prepare data for API with proper conversions
      console.log('Selected Coupon Code before save:', selectedCouponCode);
      const formData = {
        ...selectedCouponCode,
        couponcode: selectedCouponCode.couponcode || '',
        numberOfCoupons: Number(selectedCouponCode.numberOfCoupons) || 0,
        type: String(selectedCouponCode.type) || 0,
        status: String(selectedCouponCode.status) || 1,
        reservationType: String(selectedCouponCode.reservationType) || 0,
        branches: selectedCouponCode.branches.map((b) => String(typeof b === 'object' ? b.branchId || b.id : b)),
        channels: selectedCouponCode.channels.map(c => c.toString()),
        groupCodes: selectedCouponCode.groupCodes.map((g) => String(typeof g === 'object' ? g.groupCodeInformationId || g.id : g)),
      };

      // Validate
      await couponSchema.validate(formData);

      // Convert to string format for API after validation
    //   formData.type = formData.type.toString();
    //   formData.status = 'active';
    //   formData.reservationType = formData.reservationType.toString();
    //  formData.channels = formData.channels.map(c => c.toString());

      setIsSaving(true);

      // API Call
      let response;
      if (isUpdate) {
        response = await updateCouponCode(formData);
      } else {
        response = await createCouponCode(formData);
      }

      if (response?.isSuccess) {
        toast.success(isUpdate ? 'Kupon kodu başarılı güncellendi' : 'Kupon kodu başarılı eklendi');
        closeDialog();
        fetchCouponCodes();
      } else {
        toast.error(response?.error?.errors?.[0] || 'İşlem başarısız');
      }
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const errors = {};
        error.inner.forEach((err) => {
          errors[err.path] = err.message;
        });
        setValidationErrors(errors);
      } else {
        toast.error('Hata: ' + (error?.message || 'İşlem sırasında hata oluştu'));
      }
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Excel Upload
  const handleExcelUpload = async () => {
    if (!excelFile) {
      toast.warning('Lütfen bir dosya seçiniz');
      return;
    }

    try {
      setIsSaving(true);
      const formDataToSend = new FormData();
      formDataToSend.append('file', excelFile);

      const response = await uploadCouponCodes(formDataToSend);

      if (response?.isSuccess) {
        toast.success('Excel dosyası başarılı yüklendi');
        closeExcelDialog();
        fetchCouponCodes();
      } else {
        toast.error(response?.error?.errors?.[0] || 'Excel yüklemesi başarısız');
      }
    } catch (error) {
      toast.error('Hata: ' + (error?.message || 'Excel yüklenirken hata oluştu'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Input Change Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedCouponCode((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleDateChange = (dateKey, newValue) => {
    const dateString = newValue ? newValue.format(DATE_FORMAT) : '';
    setSelectedCouponCode((prev) => ({
      ...prev,
      [dateKey]: dateString,
    }));

    // Clear validation error for this field
    if (validationErrors[dateKey]) {
      setValidationErrors((prev) => ({
        ...prev,
        [dateKey]: undefined,
      }));
    }
  };

  const handleMultiSelectChange = (name, newValue) => {
    setSelectedCouponCode((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (name, newValue) => {
    setSelectedCouponCode((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // DataGrid Columns
  const columns = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    { field: 'code', headerName: 'Kupon Kodu', width: 150 },
    { field: 'name', headerName: 'Kupon Adı', width: 200 },
    { field: 'validityStartDate', headerName: 'Başlangıç Tarihi', width: 150 },
    { field: 'validityEndDate', headerName: 'Bitiş Tarihi', width: 150 },
    {
      field: 'status',
      headerName: 'Durum',
      width: 100,
      renderCell: (params) => {
        const status = STATUS_OPTIONS.find((s) => s.value === params.row.status);
        return status?.name || '-';
      },
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button variant="contained" color="primary" size="small" onClick={() => openUpdateDialog(params.row)}>
          Güncelle
        </Button>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="tr">
      <Box sx={{ padding: 2 }}>
        {/* Top Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
          <Button variant="contained" color="primary" onClick={openCreateDialog}>
            + Yeni Kupon Kodu Ekle
          </Button>
          <Button variant="contained" color="secondary" onClick={() => setOpenExcelDialog(true)}>
            📄 Excel ile Toplu Kupon Yükle
          </Button>
        </Box>

        {/* DataGrid */}
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={couponCodes}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
          />
        </Box>

        {/* Create/Update Dialog */}
        <Dialog open={openDialog} onClose={closeDialog} maxWidth="xl" fullWidth>
          <DialogTitle>{isUpdate ? 'Kupon Kodu Güncelle' : 'Yeni Kupon Kodu Ekle'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 6  }}>
            {/* Kupon Kodu */}
            <br></br>
            <TextField
              fullWidth
              label="Kupon Kodu"
              name="couponcode"
              value={selectedCouponCode.couponcode}
              onChange={handleInputChange}
              error={!!validationErrors.couponcode}
              helperText={validationErrors.couponcode}
              autoFocus
            />

            {/* Kupon Adı */}
            <TextField
              fullWidth
              label="Kupon Adı"
              name="name"
              value={selectedCouponCode.name}
              onChange={handleInputChange}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
            />

            {/* Kupon Sayısı */}
            <TextField
              fullWidth
              label="Kupon Sayısı"
              name="numberOfCoupons"
              type="number"
              value={selectedCouponCode.numberOfCoupons}
              onChange={handleInputChange}
              error={!!validationErrors.numberOfCoupons}
              helperText={validationErrors.numberOfCoupons}
            />

            {/* Başlangıç Tarihi */}
            <DatePicker
              label="Geçerlilik Başlangıç Tarihi"
              value={selectedCouponCode.validityStartDate ? moment(selectedCouponCode.validityStartDate, DATE_FORMAT) : null}
              onChange={(newValue) => handleDateChange('validityStartDate', newValue)}
              format={DATE_FORMAT}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!validationErrors.validityStartDate}
                  helperText={validationErrors.validityStartDate}
                />
              )}
            />

            {/* Bitiş Tarihi */}
            <DatePicker
              label="Geçerlilik Bitiş Tarihi"
              value={selectedCouponCode.validityEndDate ? moment(selectedCouponCode.validityEndDate, DATE_FORMAT) : null}
              onChange={(newValue) => handleDateChange('validityEndDate', newValue)}
              format={DATE_FORMAT}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!validationErrors.validityEndDate}
                  helperText={validationErrors.validityEndDate}
                />
              )}
            />

            {/* Başlangıç Tarihi (startDate) */}
            <DatePicker
              label="Başlangıç Tarihi"
              value={selectedCouponCode.startDate ? moment(selectedCouponCode.startDate, DATE_FORMAT) : null}
              onChange={(newValue) => handleDateChange('startDate', newValue)}
              format={DATE_FORMAT}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!validationErrors.startDate}
                  helperText={validationErrors.startDate}
                />
              )}
            />

            {/* Bitiş Tarihi (endDate) */}
            <DatePicker
              label="Bitiş Tarihi"
              value={selectedCouponCode.endDate ? moment(selectedCouponCode.endDate, DATE_FORMAT) : null}
              onChange={(newValue) => handleDateChange('endDate', newValue)}
              format={DATE_FORMAT}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!validationErrors.endDate}
                  helperText={validationErrors.endDate}
                />
              )}
            />

            {/* Sonra Öde İndirim Oranı */}
            <TextField
              fullWidth
              label="Sonra Öde İndirim Oranı"
              name="payLaterDiscountValue"
              type="number"
              value={selectedCouponCode.payLaterDiscountValue}
              onChange={handleInputChange}
              error={!!validationErrors.payLaterDiscountValue}
              helperText={validationErrors.payLaterDiscountValue}
            />

            {/* Hemen Öde İndirim Oranı */}
            <TextField
              fullWidth
              label="Hemen Öde İndirim Oranı"
              name="payNowDiscountValue"
              type="number"
              value={selectedCouponCode.payNowDiscountValue}
              onChange={handleInputChange}
              error={!!validationErrors.payNowDiscountValue}
              helperText={validationErrors.payNowDiscountValue}
            />

            {/* Kupon Tipi */}
            <FormControl fullWidth error={!!validationErrors.type}>
              <InputLabel>Kupon Tipi</InputLabel>
              <Select
                value={selectedCouponCode.type}
                onChange={(e) => handleSelectChange('type', e.target.value)}
                label="Kupon Tipi"
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.type && <p style={{ color: '#d32f2f', margin: '4px 14px 0', fontSize: '0.75rem' }}>{validationErrors.type}</p>}
            </FormControl>

            {/* Kupon Durumu */}
            <FormControl fullWidth error={!!validationErrors.status}>
              <InputLabel>Kupon Durumu</InputLabel>
              <Select
                value={selectedCouponCode.status}
                onChange={(e) => handleSelectChange('status', e.target.value)}
                label="Kupon Durumu"
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.status && <p style={{ color: '#d32f2f', margin: '4px 14px 0', fontSize: '0.75rem' }}>{validationErrors.status}</p>}
            </FormControl>

            {/* Rezervasyon Tipi */}
            <FormControl fullWidth error={!!validationErrors.reservationType}>
              <InputLabel>Rezervasyon Tipi</InputLabel>
              <Select
                value={selectedCouponCode.reservationType}
                onChange={(e) => handleSelectChange('reservationType', e.target.value)}
                label="Rezervasyon Tipi"
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {RESERVATION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.reservationType && <p style={{ color: '#d32f2f', margin: '4px 14px 0', fontSize: '0.75rem' }}>{validationErrors.reservationType}</p>}
            </FormControl>

            {/* Geçerli Ofisler */}
            <FormControl fullWidth error={!!validationErrors.branches}>
              <InputLabel>Geçerli Ofisler</InputLabel>
              <Select
                multiple
                value={selectedCouponCode.branches}
                onChange={(e) => handleMultiSelectChange('branches', e.target.value)}
                input={<OutlinedInput label="Geçerli Ofisler" />}
                renderValue={(selected) =>
                  selected
                    .map((val) => {
                      const found = branchesOptions.find((b) => b.branchId === val || b.id === val);
                      return found?.branchName || found?.name || String(val);
                    })
                    .join(', ')
                }
              >
                {branchesOptions.map((option) => (
                  <MenuItem key={option.branchId || option.id} value={option.branchId || option.id}>
                    <Checkbox
                      checked={selectedCouponCode.branches.includes(option.branchId || option.id)}
                    />
                    <ListItemText primary={option.branchName || option.name} />
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.branches && <p style={{ color: '#d32f2f', margin: '4px 14px 0', fontSize: '0.75rem' }}>{validationErrors.branches}</p>}
            </FormControl>

            {/* Geçerli Kanallar */}
            <FormControl fullWidth error={!!validationErrors.channels}>
              <InputLabel>Geçerli Kanallar</InputLabel>
              <Select
                multiple
                value={selectedCouponCode.channels}
                onChange={(e) => handleMultiSelectChange('channels', e.target.value)}
                input={<OutlinedInput label="Geçerli Kanallar" />}
                renderValue={(selected) =>
                  selected
                    .map((val) => {
                      const found = CHANNEL_OPTIONS.find((c) => c.value === val);
                      return found?.name || String(val);
                    })
                    .join(', ')
                }
              >
                {CHANNEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={selectedCouponCode.channels.includes(option.value)}
                    />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.channels && <p style={{ color: '#d32f2f', margin: '4px 14px 0', fontSize: '0.75rem' }}>{validationErrors.channels}</p>}
            </FormControl>

            {/* Grup Kodları */}
            <FormControl fullWidth error={!!validationErrors.groupCodes}>
              <InputLabel>Grup Kodları</InputLabel>
              <Select
                multiple
                value={selectedCouponCode.groupCodes}
                onChange={(e) => handleMultiSelectChange('groupCodes', e.target.value)}
                input={<OutlinedInput label="Grup Kodları" />}
                renderValue={(selected) =>
                  selected
                    .map((val) => {
                      const found = groupCodeOptions.find(
                        (g) => g.groupCodeInformationId === val || g.id === val
                      );
                      return found?.groupCodeInformationName || found?.name || String(val);
                    })
                    .join(', ')
                }
              >
                {groupCodeOptions.map((option) => (
                  <MenuItem key={option.groupCodeInformationId || option.id} value={option.groupCodeInformationId || option.id}>
                    <Checkbox
                      checked={selectedCouponCode.groupCodes.includes(
                        option.groupCodeInformationId || option.id
                      )}
                    />
                    <ListItemText primary={option.groupCodeInformationName || option.name} />
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.groupCodes && <p style={{ color: '#d32f2f', margin: '4px 14px 0', fontSize: '0.75rem' }}>{validationErrors.groupCodes}</p>}
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button onClick={closeDialog} disabled={isSaving}>
              İptal
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" disabled={isSaving}>
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Kaydediliyor...
                </>
              ) : (
                'Kaydet'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Excel Upload Dialog */}
        <Dialog open={openExcelDialog} onClose={closeExcelDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Excel ile Toplu Kupon Yükle</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <p>Lütfen kupon kodlarını içeren Excel dosyasını (.xlsx veya .xls) yükleyiniz.</p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
              style={{ marginTop: '16px', width: '100%' }}
            />
            {excelFile && <p style={{ marginTop: '12px', color: '#2196F3' }}>Seçilen: {excelFile.name}</p>}
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button onClick={closeExcelDialog} disabled={isSaving}>
              İptal
            </Button>
            <Button onClick={handleExcelUpload} variant="contained" color="primary" disabled={isSaving || !excelFile}>
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Yükleniyor...
                </>
              ) : (
                'Yükle'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CouponCodeManagement;
